import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { personLabel } from '../../components/ApprovalLevels.js';

interface Metric {
  readonly metricId: string;
  readonly metricCode: string;
  readonly metricValue: string;
  readonly periodKey: number;
}

interface Contributor {
  readonly userId: string;
  readonly contributions: number;
  /** The contributor, named by the server (G18). */
  readonly actorName: string | null;
  readonly actorCode: string | null;
}

/** M27 — Collaboration Analytics Dashboard (§31). Shows the current-period
 * collaboration KPI snapshot + top contributors (§20). */
export function M27CollaborationAnalyticsPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<Metric[]>([]);
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      apiRequest<{ data: Metric[] }>('/v1/collab/analytics/metrics', { token }),
      apiRequest<{ data: Contributor[] }>('/v1/collab/analytics/top-contributors', { token }),
    ])
      .then(([m, c]) => { if (alive) { setRows(m.data); setContributors(c.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const labelFor = (code: string): string => ({
    MESSAGE_COUNT: t('m27.analytics.mMessages'), OPEN_DISCUSSIONS: t('m27.analytics.mOpenDiscussions'),
    PUBLISHED_ARTICLES: t('m27.analytics.mPublishedArticles'), PUBLISHED_ANNOUNCEMENTS: t('m27.analytics.mPublishedAnnouncements'),
    ACTIVE_USERS: t('m27.analytics.mActiveUsers'), ARTICLES_CREATED: t('m27.analytics.mArticlesCreated'),
    ARTICLE_USAGE: t('m27.analytics.mArticleUsage'), DISCUSSION_PARTICIPATION: t('m27.analytics.mDiscussionParticipation'),
    RESPONSE_TIME_AVG_SEC: t('m27.analytics.mResponseTime'), KNOWLEDGE_ADOPTION_PCT: t('m27.analytics.mKnowledgeAdoption'),
  }[code] ?? code);

  return (
    <main aria-label="collaboration-analytics">
      <h1>{t('m27.analytics.heading')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m27.analytics.loading')}</p> : (
        <>
          <p aria-label="metric-count">{t('m27.analytics.metricCount', { count: rows.length })}</p>
          <table aria-label="metric-table">
            <thead><tr><th>{t('m27.analytics.colMetric')}</th><th>{t('m27.analytics.colValue')}</th><th>{t('m27.analytics.colPeriod')}</th></tr></thead>
            <tbody>{rows.map((m) => <tr key={m.metricId}><td>{labelFor(m.metricCode)}</td><td>{m.metricValue}</td><td>{m.periodKey}</td></tr>)}</tbody>
          </table>

          <section aria-label="top-contributors">
            <h2>{t('m27.analytics.topContributors')}</h2>
            <p aria-label="contributor-count">{t('m27.analytics.contributorCount', { count: contributors.length })}</p>
            {contributors.length > 0 && (
              <table aria-label="contributor-table">
                <thead><tr><th>{t('m27.analytics.colRank')}</th><th>{t('m27.analytics.colUser')}</th><th>{t('m27.analytics.colContributions')}</th></tr></thead>
                <tbody>{contributors.map((c, i) => <tr key={c.userId}><td>{i + 1}</td><td>{personLabel(c.actorName, c.actorCode) ?? '—'}</td><td>{c.contributions}</td></tr>)}</tbody>
              </table>
            )}
          </section>
        </>
      )}
    </main>
  );
}
